import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from 'react-query';
import toast from 'react-hot-toast';
import { User, Mail, Shield, MapPin, Lock, Key, CheckCircle } from 'lucide-react';
import { apiService } from '../services/api';
import { PageSkeleton } from '../components/SkeletonLoader';

function Account() {
    const { t } = useTranslation();
    const { data: currentUserData, isLoading: loadingUser } = useQuery(
        'current-user',
        () => apiService.getCurrentUser(),
        {
            refetchOnWindowFocus: false,
            staleTime: 5 * 60 * 1000,
            select: (data) => data?.user || data
        }
    );
    const currentUser = currentUserData;

    const { data: userLocations = [], isLoading: loadingLocations } = useQuery(
        ['my-locations', currentUser?.id],
        () => apiService.getUserLocations(currentUser.id),
        {
            enabled: !!currentUser?.id,
            refetchOnWindowFocus: false,
            select: (data) => data?.locations || []
        }
    );

    const [passwordForm, setPasswordForm] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });

    const changePasswordMutation = useMutation(
        ({ currentPassword, newPassword }) => apiService.changePassword(currentPassword, newPassword),
        {
            onSuccess: () => {
                toast.success(t('account.passwordSuccess', 'Password updated successfully.'));
                setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
            },
            onError: (error) => {
                const message = error?.response?.data?.error || t('account.passwordError', 'Failed to update password');
                toast.error(message);
            }
        }
    );

    const handlePasswordSubmit = (e) => {
        e.preventDefault();
        if (passwordForm.newPassword !== passwordForm.confirmPassword) {
            toast.error(t('account.passwordMismatch', 'New passwords do not match.'));
            return;
        }
        changePasswordMutation.mutate({
            currentPassword: passwordForm.currentPassword,
            newPassword: passwordForm.newPassword
        });
    };

    if (loadingUser) {
        return <PageSkeleton type="account" />;
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-3 sm:p-6 space-y-4 sm:space-y-6 max-w-full overflow-x-hidden">
            {/* Header */}
            <div className="flex flex-col gap-2">
                <h1 className="text-3xl font-bold text-gray-900">{t('account.title', 'My Account')}</h1>
                <p className="text-sm text-gray-500">
                    {t('account.subtitle', 'Manage your personal settings and access')}
                </p>
            </div>

            {/* Profile Overview Cards */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {/* Email Card */}
                <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-blue-200 via-blue-300 to-indigo-300 p-6 shadow-lg">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-indigo-900">{t('account.fields.email', 'Email')}</p>
                            <p className="mt-2 text-lg font-bold text-indigo-950 break-all">{currentUser?.email}</p>
                        </div>
                        <Mail className="h-10 w-10 text-indigo-700 opacity-60" />
                    </div>
                </div>

                {/* Role Card */}
                <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-purple-200 via-purple-300 to-pink-300 p-6 shadow-lg">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-purple-900">{t('account.fields.role', 'Role')}</p>
                            <p className="mt-2 text-lg font-bold text-purple-950 capitalize">
                                {t(`roles.${currentUser?.role}`, currentUser?.role)}
                            </p>
                        </div>
                        <Shield className="h-10 w-10 text-purple-700 opacity-60" />
                    </div>
                </div>

                {/* Locations Count Card */}
                <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-green-200 via-emerald-200 to-teal-300 p-6 shadow-lg">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-green-900">{t('account.locationsHeading', 'Accessible Locations')}</p>
                            <p className="mt-2 text-lg font-bold text-green-950">
                                {userLocations.length > 0 ? userLocations.length : t('common.all', 'All')}
                            </p>
                        </div>
                        <MapPin className="h-10 w-10 text-green-700 opacity-60" />
                    </div>
                </div>
            </div>

            {/* Profile Details */}
            <div className="rounded-xl bg-white p-6 shadow-sm border border-gray-200">
                <div className="flex items-center gap-3 mb-6">
                    <div className="rounded-lg bg-gradient-to-br from-indigo-500 to-indigo-600 p-2">
                        <User className="h-5 w-5 text-white" />
                    </div>
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900">
                            {t('account.profileHeading', 'Profile')}
                        </h2>
                        <p className="text-sm text-gray-500">
                            {t('account.profileInfo', 'Your account information')}
                        </p>
                    </div>
                </div>

                <div className="grid gap-6 md:grid-cols-2">
                    <div className="space-y-4">
                        <div className="flex items-start gap-3 p-4 rounded-lg bg-gray-50 border border-gray-200">
                            <Mail className="h-5 w-5 text-gray-600 mt-0.5" />
                            <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                                    {t('account.fields.email', 'Email')}
                                </p>
                                <p className="mt-1 text-sm font-medium text-gray-900 break-all">{currentUser?.email}</p>
                            </div>
                        </div>

                        <div className="flex items-start gap-3 p-4 rounded-lg bg-gray-50 border border-gray-200">
                            <Shield className="h-5 w-5 text-gray-600 mt-0.5" />
                            <div className="flex-1">
                                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                                    {t('account.fields.role', 'Role')}
                                </p>
                                <p className="mt-1 text-sm font-medium text-gray-900 capitalize">
                                    {t(`roles.${currentUser?.role}`, currentUser?.role)}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-start gap-3 p-4 rounded-lg bg-gray-50 border border-gray-200">
                        <MapPin className="h-5 w-5 text-gray-600 mt-0.5" />
                        <div className="flex-1">
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                                {t('account.locationsHeading', 'Accessible Locations')}
                            </p>
                            {loadingLocations ? (
                                <div className="space-y-2 animate-pulse">
                                    <div className="h-4 bg-gray-200 rounded w-2/3" />
                                    <div className="h-4 bg-gray-200 rounded w-1/2" />
                                </div>
                            ) : userLocations.length > 0 ? (
                                <ul className="space-y-2 max-h-32 overflow-y-auto">
                                    {userLocations.map(location => (
                                        <li key={location.id} className="flex items-center gap-2 text-sm text-gray-700">
                                            <span className="w-1.5 h-1.5 bg-green-500 rounded-full flex-shrink-0" />
                                            <span>{location.name}</span>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p className="text-sm text-gray-600">
                                    {t('account.noLocations', 'No specific locations assigned. You currently have access to all locations.')}
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Change Password Section */}
            <div className="rounded-xl bg-white p-6 shadow-sm border border-gray-200">
                <div className="flex items-center gap-3 mb-6">
                    <div className="rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 p-2">
                        <Lock className="h-5 w-5 text-white" />
                    </div>
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900">
                            {t('account.changePasswordHeading', 'Change Password')}
                        </h2>
                        <p className="text-sm text-gray-500">
                            {t('account.passwordHint', 'Update your password to keep your account secure')}
                        </p>
                    </div>
                </div>

                <form className="space-y-4 max-w-2xl" onSubmit={handlePasswordSubmit}>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t('account.fields.currentPassword', 'Current Password')}
                        </label>
                        <div className="relative">
                            <Key className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                            <input
                                type="password"
                                value={passwordForm.currentPassword}
                                onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                                className="w-full rounded-lg border border-gray-300 py-2.5 pl-10 pr-4 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                                required
                            />
                        </div>
                    </div>

                    <div className="grid gap-4 md:grid-cols-2">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                {t('account.fields.newPassword', 'New Password')}
                            </label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                                <input
                                    type="password"
                                    value={passwordForm.newPassword}
                                    onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                                    className="w-full rounded-lg border border-gray-300 py-2.5 pl-10 pr-4 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                                    required
                                    minLength={6}
                                />
                            </div>
                            <p className="mt-1 text-xs text-gray-500">
                                {t('account.passwordMinLength', 'Minimum 6 characters')}
                            </p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                {t('account.fields.confirmPassword', 'Confirm Password')}
                            </label>
                            <div className="relative">
                                <CheckCircle className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                                <input
                                    type="password"
                                    value={passwordForm.confirmPassword}
                                    onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                                    className="w-full rounded-lg border border-gray-300 py-2.5 pl-10 pr-4 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                                    required
                                    minLength={6}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end pt-2">
                        <button
                            type="submit"
                            disabled={changePasswordMutation.isLoading}
                            className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-indigo-600 to-indigo-700 px-6 py-2.5 text-sm font-semibold text-white shadow-lg hover:from-indigo-700 hover:to-indigo-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                        >
                            {changePasswordMutation.isLoading ? (
                                <>
                                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                                    {t('account.actions.updating', 'Updating...')}
                                </>
                            ) : (
                                <>
                                    <Lock className="h-4 w-4" />
                                    {t('account.actions.updatePassword', 'Update Password')}
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default Account;
